import { ArrowLeft, ArrowRight, BriefcaseBusiness, Check, Download, GraduationCap, HeartPulse, Home, Laptop, Loader2, Monitor, Wrench } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import type { Lang } from "../i18n";
import { DownloadApiError, requestDownload, type DownloadOption } from "../downloadApi";
import { startTrackedDownload } from "../downloadTracking";
import { downloadReturnPath } from "../routes";
import { BrandLogo } from "./BrandLogo";
import { Seo } from "./Seo";

const industryOptions: DownloadOption[] = [
  { industry_code: "clinic", industry_name: "综合诊所", description: "门诊、专科与社区医疗", icon: "health", available: false },
  { industry_code: "dental", industry_name: "牙科诊所", description: "牙科、正畸与口腔护理", icon: "health", available: false },
  { industry_code: "aesthetics", industry_name: "医美与美容", description: "医美、美容院与护理服务", icon: "health", available: false },
  { industry_code: "veterinary", industry_name: "宠物医疗", description: "宠物医院、诊所与护理", icon: "health", available: false },
  { industry_code: "legal", industry_name: "法律服务", description: "律师事务所与法律咨询", icon: "business", available: false },
  { industry_code: "accounting", industry_name: "财税与会计", description: "会计、税务与财务咨询", icon: "business", available: false },
  { industry_code: "consulting", industry_name: "商业咨询", description: "顾问、代理与专业服务", icon: "business", available: false },
  { industry_code: "education", industry_name: "教育培训", description: "课程、试听与报名咨询", icon: "education", available: false },
  { industry_code: "fitness", industry_name: "健身与运动", description: "健身房、工作室与私教", icon: "health", available: false },
  { industry_code: "home_services", industry_name: "上门与维修", description: "维修、安装、清洁与家政", icon: "tools", available: false },
  { industry_code: "hospitality", industry_name: "酒店与住宿", description: "酒店、民宿与短租服务", icon: "home", available: false },
  { industry_code: "real_estate", industry_name: "房地产", description: "房产经纪、租赁与物业", icon: "home", available: false },
  { industry_code: "automotive", industry_name: "汽车服务", description: "维修、保养与汽车美容", icon: "tools", available: false },
  { industry_code: "other", industry_name: "其他行业", description: "未在列表中找到你的行业", icon: "business", available: false },
];

const fallbackEnglish: Record<string, { name: string; description: string }> = {
  clinic: { name: "Medical clinics", description: "Primary, specialty, and community care" },
  dental: { name: "Dental practices", description: "Dental, orthodontic, and oral care" },
  aesthetics: { name: "Aesthetics & beauty", description: "Aesthetic clinics, salons, and care services" },
  veterinary: { name: "Veterinary care", description: "Veterinary hospitals, clinics, and pet care" },
  legal: { name: "Legal services", description: "Law firms and legal consultations" },
  accounting: { name: "Accounting & tax", description: "Accounting, tax, and financial advisory" },
  consulting: { name: "Business consulting", description: "Consulting, agencies, and professional services" },
  home_services: { name: "Home & field services", description: "Home, repair, installation, and local services" },
  education: { name: "Education & training", description: "Course enquiries, trials, and enrollment" },
  fitness: { name: "Fitness & sports", description: "Gyms, studios, and personal training" },
  hospitality: { name: "Hotels & hospitality", description: "Hotels, rentals, and guest services" },
  real_estate: { name: "Real estate", description: "Brokerage, rentals, and property services" },
  automotive: { name: "Automotive services", description: "Repair, maintenance, and vehicle care" },
  other: { name: "Other industries", description: "Choose this if your industry is not listed" },
};

const icons = { health: HeartPulse, business: BriefcaseBusiness, tools: Wrench, education: GraduationCap, home: Home };

const platformOptions = [
  { id: "windows-x64", platform: "windows", architecture: "x64", zh: "Windows", en: "Windows", hintZh: "64 位 Windows", hintEn: "64-bit Windows", icon: Monitor },
  { id: "macos-arm64", platform: "macos", architecture: "arm64", zh: "macOS", en: "macOS", hintZh: "Apple 芯片", hintEn: "Apple silicon", icon: Laptop },
  { id: "macos-x64", platform: "macos", architecture: "x64", zh: "macOS", en: "macOS", hintZh: "Intel 芯片", hintEn: "Intel processor", icon: Laptop },
] as const;

export function DownloadPage({ defaultLang = "en" as Lang }: { defaultLang?: Lang }) {
  const lang = defaultLang;
  const [params] = useSearchParams();
  const { search } = useLocation();
  const backPath = downloadReturnPath(lang, search);
  const [selected, setSelected] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const downloadInFlight = useRef(false);
  const [error, setError] = useState("");

  const options = useMemo(() => industryOptions.map((item) => {
    const english = fallbackEnglish[item.industry_code];
    return lang === "zh" || !english ? item : { ...item, industry_name: english.name, description: english.description };
  }), [lang]);

  const selectedOption = useMemo(() => options.find((item) => item.industry_code === selected), [options, selected]);
  const platformOption = platformOptions.find((item) => item.id === selectedPlatform);

  const download = async () => {
    if (!selectedOption || !platformOption || downloadInFlight.current) return;
    downloadInFlight.current = true;
    setError("");
    setSubmitting(true);
    try {
      const result = await requestDownload(selectedOption.industry_code, platformOption.platform, platformOption.architecture, lang, params);
      await startTrackedDownload(result.download_url);
    } catch (cause) {
      if (cause instanceof DownloadApiError && cause.reason === "timeout") {
        setError(lang === "zh" ? "获取下载地址超时，请稍后重试。" : "The download request timed out. Please try again.");
      } else {
        const detail = cause instanceof DownloadApiError && cause.message ? ` (${cause.message})` : "";
        setError(lang === "zh" ? `暂时无法获取下载地址，请稍后重试。${detail}` : `Unable to retrieve the download URL. Please try again later.${detail}`);
      }
    } finally {
      downloadInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f8f9fa] px-5 py-8 text-[#100f12] sm:px-10">
      <Seo lang={lang} page="download" />
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(133,75,232,.18),transparent_38%)]" />
      <div className="relative mx-auto max-w-[980px]">
        <header className="flex items-center justify-between">
          <Link to={backPath} className="rounded-md no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/60 focus-visible:ring-offset-2"><BrandLogo /></Link>
          <Link to={backPath} className="inline-flex items-center gap-2 text-sm font-bold text-[#5f5565] no-underline hover:text-[#5b24c9]"><ArrowLeft size={16} />{lang === "zh" ? "返回官网" : "Back to website"}</Link>
        </header>

        <section className="mt-12 rounded-[28px] border border-[#eaddec] bg-white px-5 py-8 shadow-[0_28px_80px_rgba(41,20,58,.10)] sm:px-10 sm:py-11">
          <div className="mx-auto max-w-[680px] text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#f3eefa] px-3 py-1 text-xs font-extrabold text-[#5b24c9]"><Download size={14} />ClawEase Download</span>
            <h1 className="mt-4 text-3xl font-black tracking-[-0.04em] sm:text-5xl">{lang === "zh" ? "选择你所在的行业" : "Select your industry"}</h1>
            <p className="mt-4 text-sm font-semibold leading-7 text-[#6b6170] sm:text-base">{lang === "zh" ? "请选择最符合你业务的行业。" : "Choose the industry that best matches your business."}</p>
          </div>

          <div className="mx-auto mt-8 grid max-w-[780px] gap-3 sm:grid-cols-2">
            {options.map((option) => {
              const Icon = icons[option.icon as keyof typeof icons] || BriefcaseBusiness;
              const active = selected === option.industry_code;
              return <button key={option.industry_code} type="button" onClick={() => setSelected(option.industry_code)} className={`relative flex items-center gap-4 rounded-2xl border p-4 text-left transition-all ${active ? "border-[#5b24c9] bg-[#faf7ff] ring-2 ring-[#5b24c9]/15" : "border-[#eaddec] bg-white hover:-translate-y-0.5 hover:border-[#cbb4e6]"}`}>
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#f3eefa] text-[#5b24c9]"><Icon size={21} /></span>
                <span className="min-w-0"><strong className="block text-[15px] font-extrabold">{option.industry_name}</strong><span className="mt-1 block text-xs font-semibold leading-5 text-[#817687]">{option.description}</span></span>
                {active ? <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#5b24c9] text-white"><Check size={13} strokeWidth={3} /></span> : null}
              </button>;
            })}
          </div>

          <div className="mx-auto mt-7 max-w-[780px]">
            <h2 className="text-center text-lg font-black">{lang === "zh" ? "选择下载版本" : "Select your download"}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {platformOptions.map((option) => {
                const Icon = option.icon;
                const active = selectedPlatform === option.id;
                return <button key={option.id} type="button" onClick={() => setSelectedPlatform(option.id)} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${active ? "border-[#5b24c9] bg-[#faf7ff] ring-2 ring-[#5b24c9]/15" : "border-[#eaddec] bg-white hover:border-[#cbb4e6]"}`}>
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#f3eefa] text-[#5b24c9]"><Icon size={18} /></span>
                  <span><strong className="block text-sm font-extrabold">{lang === "zh" ? option.zh : option.en}</strong><span className="mt-0.5 block text-xs font-semibold text-[#817687]">{lang === "zh" ? option.hintZh : option.hintEn}</span></span>
                  {active ? <Check size={16} className="ml-auto text-[#5b24c9]" strokeWidth={3} /> : null}
                </button>;
              })}
            </div>
          </div>

          <div className="mx-auto mt-7 max-w-[780px]">
            <button type="button" disabled={!selected || !selectedPlatform || submitting} onClick={() => void download()} className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#5b24c9] px-6 text-sm font-extrabold text-white shadow-[0_18px_42px_rgba(91,36,201,.24)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0">
              {submitting ? <Loader2 size={17} className="animate-spin" /> : <Download size={17} />}
              {lang === "zh" ? "确认并下载" : "Confirm and download"}<ArrowRight size={17} />
            </button>
            {error ? <p className="mt-3 text-center text-sm font-semibold text-red-600">{error}</p> : null}
            <p className="mt-3 text-center text-xs font-semibold text-[#978c9c]">{lang === "zh" ? "自动获取当前正式发布版本。" : "Automatically downloads the current production release."}</p>
          </div>
        </section>
      </div>
    </main>
  );
}
